/**
 * weaponAdapter.ts — Bridges the weapon system (weapons.ts + weaponSystem.ts)
 * to the engine's WeaponsModule interface.
 *
 * This adapter creates and manages WeaponSystemState internally,
 * and exposes it through the registerWeapons() pattern the engine uses.
 *
 * The weapon system's renderWeapons() does its own world→screen conversion,
 * so it must be called OUTSIDE the camera transform block.
 */

import type { GameState, ViewBounds, WeaponsModule } from './types';
import { WEAPON_DEPTH_GROUP, MAP_PROPS, BOUNDLESS_MAP } from './mapChunks';
import { propsInRect, propAspect } from './worldProps';   // BETA 23.13   // BETA 23.01 — weapon depth kill-switch
import { isWalkable, hasLineOfSight } from './types';
import {
  type WeaponSystemState,
  type CameraForWeapon,
  type PlayerForWeapon,
  type EnemyForWeapon,
  createWeaponSystemState,
  equipWeapon,
  updateWeapons,
  renderWeapons,
  renderArcSlashes,
  renderArcWeaponIdle,
  renderAuraInWorldSpace,
  getReloadProgress,
  getAmmoDisplay,
  getEquippedWeapon,
  spawnImpactAoe,
  spawnImpactExplosion,
  renderOrbitWeaponSprites, setWeaponOcclusionZones, type WeaponOcclusionZone,
} from './weaponSystem';
import { getStarterWeapon, levelUpWeapon, isWeaponThrowable, isWeaponAura, isWeaponArc } from './weapons';
import { getEnemySystemState } from './enemyAdapter';
import { damageEnemy as damageEnemyFn, applyEnemyStatus as applyEnemyStatusFn, applyEnemyArmorShieldStrip as applyEnemyArmorShieldStripFn } from './enemySystem';
import { getAudioContext, getSfxGainNode } from './audio';
import {
  initGameSfx,
  updateAudioListener,
  playRevolverShot,
  playRpkShot,
  // BETA 15.93 — playRevolverReload + playRifleReload imports removed.
  // Reload SFX silenced per Dylan ("lets get rid of reload sounds. its to
  // much."). Functions still exist in gameSfx.ts for the Sound Inspector.
  playRifleShot,
  playSpatulaSlash,
  playBasketballBounce,
  playEnemyHit,
  playEnemyDeath,
  // BETA 15.74 — Audio Batch #1 (PSBOT RUN) + Dylan cat sounds
  // Ultra Beam audio is fired from weaponSystem at charge-up start (not here)
  playBlunderbussFire,
  playVikingAxeThrow,
  playVoogooRagerThrow,
  playFireballWandFire,
  playTerminatorFire,
  playKittensSpawn,
  // BETA 15.75 — Dylan-provided poop sling sounds
  playPoopSlingFire,
  // BETA 15.76 — Dylan-provided squirter fire (no reload — continuous stream)
  playSquirterFire,
  // BETA 15.77 — Frozen Darts, Drac's Fang (melee swing — wired via arcSlash
  // detection like spatula), Shitty Cannon, Ex Girlfriend (5 overlapping shots).
  playFrozenDartsFire,
  playDracsFangSwing,
  playShittyCannonFire,
  playExGirlfriendFire,
  // BETA 15.78 — Hurray fire
  playHurrayFire,
  playSmgFire,
  playRpgFire,
  playCrossbowFire,
  playRailgunFire,
  playChainWhipSwing,
  // BETA — per-weapon impact sound (fireball/axe/cannon/frozen/terminator/spatula/ultra-beam)
  playWeaponHit,
} from './gameSfx';
import { spawnDrops } from './lootOrbs';
import { recordKill } from './performanceTracker';
import { debugPlayerDamageMult } from './debugState';
import { recordItemKill, getItemState } from './itemsAdapter';
import { tuneOf } from './bunchaBullshiiii';   // BETA 21.81 — editable item effect magnitudes
import { spawnCritExplosion, isPositionPoisoned, spawnAnusRocket } from './itemEffectSystem';
import { playAnusRocket } from './gameSfx';
// BETA 15.26 — character passive kill hook (Sniper proc, Silly stack +,
// Surge counter, Scavenger drops, Chad gold-in-aura). Returns extra drops
// to append to standard loot.
import { onPlayerKilledEnemy } from './characterPassives';
// BETA 22.72 — flame-cone roar rate limiter
let _flameRoarLast = -10;
// BETA 22.70 — weaponVfx hit effects (visual burn + ground decals)
import { getWeaponVfx } from './weaponVfx';
import { spawnGroundDecal } from './groundDecals';
// BETA 17.09 — arc aim assist for bot runs (Dylan: "make bots smart as me").
import { isBotEnabled as _isBotEnabledForArcAssist } from './autoPlaytester';

let sfxInitialized = false;

// ─── Internal State ─────────────────────────────────────────────────
let weaponState: WeaponSystemState | null = null;
let canvasWidth = 1280;
let canvasHeight = 720;

// ─── Per-run damage accumulator ──────────────────────────────────────
let _runDamageDealt = 0;
export function getRunDamageDealt(): number { return _runDamageDealt; }
export function resetRunDamageDealt(): void { _runDamageDealt = 0; }

// ─── Facing Angle Tracking (for arc/melee weapons) ──────────────────
// Updated every frame from player position delta. Persists last known
// movement direction so spatula still swings sensibly when standing still.
let lastFacingAngle = 0;  // radians; 0 = right
let prevPlayerX = 0;
let prevPlayerY = 0;

// BETA 19.44 (live audit fix) — Bot smart-aim for arc weapons that use
// arcLiveDirection (Spatula). Set each frame by the bot aim-assist block
// below to the angle pointing at the nearest in-reach enemy. Null when
// bot is inactive or no enemy in reach — falls through to the PSBOT
// movement-vector spec for humans. Read by getMovementAimAngle in
// weaponSystem.ts via PlayerForWeapon.arcAimOverride.
let lastArcAimOverride: number | null = null;

// ─── Public: set canvas dimensions (called from GameCanvas) ─────────
export function setWeaponCanvasDimensions(w: number, h: number) {
  canvasWidth = w;
  canvasHeight = h;
}

// ─── Public: get player's last facing angle (for enemy spawn bias) ───
export function getLastFacingAngle(): number { return lastFacingAngle; }

// ─── Public: get raw state for debug / external access ──────────────
export function getWeaponSystemState(): WeaponSystemState | null {
  return weaponState;
}

// ─── Public: get reload progress for HUD ────────────────────────────
export function getWeaponReloadProgress(): number {
  if (!weaponState || weaponState.weapons.length === 0) return 1;
  return getReloadProgress(weaponState.weapons[0]);
}

// ─── Public: get ammo display for HUD ───────────────────────────────
export function getWeaponAmmoDisplay(): string {
  if (!weaponState || weaponState.weapons.length === 0) return '';
  return getAmmoDisplay(weaponState.weapons[0]);
}

// ─── Public: is the primary weapon throwable, an aura, or an arc melee (no ammo counter) ───
export function getWeaponIsThrowable(): boolean {
  if (!weaponState || weaponState.weapons.length === 0) return false;
  const id = weaponState.weapons[0].definitionId;
  return isWeaponThrowable(id) || isWeaponAura(id) || isWeaponArc(id);
}

// ─── Public: level up the primary weapon ────────────────────────────
// BETA 19.2 — Optional cardRarity arg (Megabonk rarity multiplier).
// BETA 19.4 — Optional cardStats arg (Megabonk stat-pool subset).
// BETA 19.14 — Optional cardLuckMult arg (luck-magnitude scaler).
export function levelUpPrimaryWeapon(
  cardRarity?: import('./peanutPercs').UpgradeRarity,
  cardStats?: import('./weapons').WeaponStatKey[],
  cardLuckMult?: number,
  cardJitterMult?: number,
): boolean {
  if (!weaponState || weaponState.weapons.length === 0) return false;
  return levelUpWeapon(weaponState.weapons[0], cardRarity, cardStats, cardLuckMult, cardJitterMult);
}

// ─── Public: level up a weapon by definition ID ─────────────────────────────
export function levelUpWeaponById(
  weaponId: string,
  cardRarity?: import('./peanutPercs').UpgradeRarity,
  cardStats?: import('./weapons').WeaponStatKey[],
  cardLuckMult?: number,
  cardJitterMult?: number,
): boolean {
  if (!weaponState) return false;
  const instance = weaponState.weapons.find(w => w.definitionId === weaponId);
  if (!instance) return false;
  return levelUpWeapon(instance, cardRarity, cardStats, cardLuckMult, cardJitterMult);
}

// ─── Public: equip a new weapon by ID (used by level-up weapon offers) ──────
export function addWeaponById(weaponId: string): boolean {
  if (!weaponState) return false;
  // BETA 20.86 — if you ALREADY own this weapon, level it up instead of
  // pushing a duplicate level-1 instance. Dylan's level-5 RPK kept showing
  // "Lv 1 → 2" because a duplicate level-1 RPK got created and the level-ups
  // landed on the copy. Owned → level up; only truly new weapons get equipped.
  if (weaponState.weapons.some(w => w.definitionId === weaponId)) {
    return levelUpWeaponById(weaponId);
  }
  return equipWeapon(weaponState, weaponId);
}

// BETA 22.58 — dev-only weapon grant for audits ("every weapon needs to be
// audited... that it has correct code in game"). Usage in console:
//   __giveWeapon('wpn_railgun')  → equips (or levels) the weapon mid-run.
if (typeof window !== 'undefined') {
  (window as any).__giveWeapon = (id: string) => addWeaponById(id);
}

// ─── The Adapter Module ─────────────────────────────────────────────
export const weaponAdapterModule: WeaponsModule = {
  init(state: GameState) {
    weaponState = createWeaponSystemState();
    // Reset per-run accumulators
    _runDamageDealt = 0;
    // Reset facing angle tracking for new run
    lastFacingAngle = 0;
    prevPlayerX = state.player.x;
    prevPlayerY = state.player.y;

    // Map menu character IDs to weapon owner IDs
    // Home.tsx uses short IDs ('cowboy'), weapons.ts uses full IDs ('cowboy_peanut')
    const charId = state.character?.id ?? '';
    const ownerMap: Record<string, string> = {
      'cowboy':    'cowboy_peanut',
      'tactical':  'tactical_peanut',
      'sponge':    'spongebob_peanut',
      'killa':     'killa_peanut',
      'wizard':    'wizard_peanut',
      'viking':    'viking_peanut',
      'mr_silly':  'mr_silly_peanut',
      'lebron':    'lebron_peanut',
      'chad':      'chad_peanut',
      'pirate':    'pirate_peanut',
    };
    const weaponOwnerId = ownerMap[charId] ?? charId;
    const starterDef = getStarterWeapon(weaponOwnerId);

    if (starterDef) {
      equipWeapon(weaponState, starterDef.id, 'common');
      console.log(`[Weapon] Equipped starter weapon: ${starterDef.name} for ${weaponOwnerId}`);
    } else {
      // Fallback: equip revolver for any character (only weapon implemented so far)
      equipWeapon(weaponState, 'revolver', 'common');
      console.log(`[Weapon] No starter weapon for ${weaponOwnerId} (char: ${charId}), defaulting to revolver`);
    }
  },

  update(state: GameState, dt: number) {
    if (!weaponState) return;

    const { player } = state;

    // ── Update facing angle from position delta (used by arc/melee weapons) ──
    const dxP = player.x - prevPlayerX;
    const dyP = player.y - prevPlayerY;
    if (Math.abs(dxP) > 0.01 || Math.abs(dyP) > 0.01) {
      lastFacingAngle = Math.atan2(dyP, dxP);
    }
    prevPlayerX = player.x;
    prevPlayerY = player.y;

    // BETA 17.09 — Bot AI aim assist for arc weapons (Dylan: "make bots smart
    // as me"). PSBOT cycle-30z showed spatula 7.3% / dracs_fang 5.5% hit rate
    // — root cause is the bot moves toward kite vectors / waypoints, NOT toward
    // enemies, so the arc swing fires into empty space behind/beside the bot.
    // Humans naturally "move to aim" so swings connect. The bot lacks that
    // instinct, so when bot is active + has an arc weapon equipped + an enemy
    // is in lockOn range, override the facing angle to point at the nearest
    // such enemy. Human play untouched (botActive gates this).
    if (_isBotEnabledForArcAssist() && weaponState?.weapons?.length) {
      // BETA 17.17 — Per-weapon effective arc reach. Spatula 65wu, Drac's
      // Fang 140wu. Bot now prioritizes the nearest enemy WITHIN actual
      // swing range (so swings always connect on the closest target),
      // falling back to wider 300wu lock-on only if no enemy is in arc
      // reach yet. This addresses the 5-7% hit rate from cycle-30z by
      // making the bot face enemies it can actually HIT, not just enemies
      // it can SEE. Increased outer fallback to 360wu to match arc weapon
      // lockOnRange + buffer.
      // BETA 19.45 — Drac's Fang is no longer an arc weapon (converted to
      // throwable boomerang). Spatula is the only arc weapon left, so its
      // 85wu effective reach is the only entry needed here.
      const arcWeaponReach: Record<string, number> = {
        'spatula_slash': 85,        // 65 + 20wu swing buffer
      };
      let maxArcReach = 0;
      for (const w of weaponState.weapons) {
        const r = arcWeaponReach[w.definitionId];
        if (r && r > maxArcReach) maxArcReach = r;
      }
      if (maxArcReach > 0) {
        const _ess = getEnemySystemState();
        if (_ess) {
          // Primary search: enemy within arc reach (always hittable on swing)
          let nearestSqInReach = maxArcReach * maxArcReach;
          let nearestAngleInReach: number | null = null;
          // Fallback search: enemy within 360wu lock-on (bot will move toward it)
          let nearestSqLockOn = 360 * 360;
          let nearestAngleLockOn: number | null = null;
          for (const enemy of _ess.enemies) {
            if (!enemy.alive) continue;
            const dx = enemy.x - player.x;
            const dy = enemy.y - player.y;
            const distSq = dx * dx + dy * dy;
            if (distSq < nearestSqInReach) {
              nearestSqInReach = distSq;
              nearestAngleInReach = Math.atan2(dy, dx);
            } else if (distSq < nearestSqLockOn) {
              nearestSqLockOn = distSq;
              nearestAngleLockOn = Math.atan2(dy, dx);
            }
          }
          // Prefer in-reach target. Fall back to lock-on. Else leave facing alone.
          if (nearestAngleInReach !== null) {
            lastFacingAngle = nearestAngleInReach;
            // BETA 19.44 (live audit fix #2) — Dylan: bot must be smart with
            // new spatula. arcLiveDirection (movement-vector aim) sweeps AWAY
            // from enemies when bot kites. Override with in-reach enemy
            // angle so the swing connects on the closest target.
            lastArcAimOverride = nearestAngleInReach;
          } else if (nearestAngleLockOn !== null) {
            lastFacingAngle = nearestAngleLockOn;
            // BETA 19.44 (live audit fix #3) — Live evidence: bot keeps
            // enemies at 87-150wu (just past 85wu spatula-in-reach) so the
            // in-reach branch above NEVER fires. Result: spatula sweeps along
            // velocity (empty space) → 0 kills in 50s. Fix: ALSO override
            // when there's a lock-on target. Spatula sweep is wide (110°
            // base growing toward 360°) so pointing it at a 200-300wu enemy
            // catches that enemy the moment the bot drifts into reach.
            lastArcAimOverride = nearestAngleLockOn;
          } else {
            lastArcAimOverride = null;  // no enemies anywhere — pure movement aim
          }
        }
      } else {
        // No arc weapon equipped — clear any stale override from a previous loadout.
        lastArcAimOverride = null;
      }
    }

    // BETA 13.98 — check Drac's Gas Mask item presence for synergy
    const _itemState = getItemState();
    const _hasDracsGasMask = !!_itemState?.inventory.find(s => s.item.id === 'item_dracs_gas_mask');

    // Build player interface for weapon system — pull real bonuses from player stat fields
    const playerForWeapon: PlayerForWeapon = {
      x: player.x,
      y: player.y,
      facingAngle: lastFacingAngle,
      // BETA 19.44 — live velocity for Spatula's movement-direction aim
      // (PSBOT cycle-30at-ADDENDUM-2 spec). engine.ts:2552 populates vx/vy
      // each frame from totalMoveX,Y / dt — these are wu/sec.
      vx: player.vx ?? 0,
      vy: player.vy ?? 0,
      // BETA 19.44 (live audit fix) — bot smart-aim override for arc weapons.
      // Set by the bot aim-assist block above to the nearest-enemy angle when
      // an enemy is in actual swing reach. Null when bot is off OR no enemy
      // in reach (humans always see null → falls through to PSBOT movement
      // spec). weaponSystem.getMovementAimAngle checks this first.
      arcAimOverride: lastArcAimOverride,
      hasDracsGasMask: _hasDracsGasMask,
      // BETA 21.42 — fold the previously-DEAD `damageMultiplier` field into the
      // damage percent every weapon damage-calc site already reads
      // (weaponSystem.ts: weapon.damage * (1 + bonusDamagePercent/100)).
      // damageMultiplier is the transient MULTIPLICATIVE buff written by 3 ultras
      // (1000LB Momentum +200%, The Grind +3%/stack, Donation Alert +50–100%) —
      // it was never read by any damage calc, so those ultras dealt ZERO bonus
      // damage. Fold: (1+dmgBonus/100)*dmgMult = 1 + effPct/100. Single point →
      // all 7 damage sites pick it up; reverts cleanly (ultras subtract their own).
      bonusDamagePercent: (((1 + (player.damageBonus ?? 0) / 100) * (player.damageMultiplier ?? 1)) - 1) * 100,
      bonusAttackSpeedPercent: player.attackSpeedBonus ?? 0,
      bonusCritChance: player.critChance ?? 5,
      bonusCritDamage: player.critDamage ?? 0,
      // BETA 13.77 — was hardcoded 0. Now reads from the newly exposed Player fields
      // so shrines/items that grant projectile-speed or AOE-size buffs actually flow through.
      bonusProjectileSpeed: player.projectileSpeedBonus ?? 0,
      bonusProjectileSize: player.attackAreaBonus ?? 0,
      bonusKnockback: player.knockbackBonus ?? 0,
      bonusProjectileCount: player.projectileCountBonus ?? 0,
      bonusRange: player.rangeBonus ?? 0,
      // BETA 13.82 — wires `+1 Projectile Bounce` shrine into the weapon system.
      bonusProjectileBounces: player.projectileBounceBonus ?? 0,
      // BETA 19.98 — N08 Not Ritalin pierce was a DEAD STAT — perc set
      // player.projectilePierceBonus but no weapon read it. Now bridged.
      bonusProjectilePiercing: player.projectilePierceBonus ?? 0,
      // BETA 16.27 — PSBOT cycle-29 P0: explicit attackAreaBonus field for
      // arc-weapon (Spatula + Drac's Fang) sprite/sector scaling. Was
      // smuggled in via bonusProjectileSize, but the spatula render path
      // explicitly references attackAreaBonus now (correct name).
      attackAreaBonus: player.attackAreaBonus ?? 0,
    };

    // Get alive enemies from enemy system for auto-aim targeting
    const enemySystemState = getEnemySystemState();
    const aliveEnemies: EnemyForWeapon[] = [];

    if (enemySystemState) {
      for (const e of enemySystemState.enemies) {
        if (!e.alive) continue;
        aliveEnemies.push({
          id: e.id,
          type: e.definitionId,  // BETA 14.47 — needed for weaponEnemyMatrix
          x: e.x,
          y: e.y,
          hitboxRadius: e.hitboxRadius,
          alive: e.alive,
          variant: e.variant,    // BETA 22.08 — auto-aim prioritizes crowned/elite/boss
        });
      }
    }

    // The callback that bridges weapons → enemy damage
    const onEnemyHit = (
      enemyId: number,
      damage: number,
      knockbackForce: number,
      knockbackAngle: number,
      isCrit: boolean,
      weaponDefId?: string,   // BETA — for per-weapon impact SFX
    ) => {
      if (!enemySystemState) return;
      let finalDmg = damage * (debugPlayerDamageMult !== 1 ? debugPlayerDamageMult : 1);
      // BETA 13.77 — apply +X% Elite Damage shrine/item bonus when target is an elite variant.
      // Looks up the target on the live enemy state so we don't need a second variant carry-through.
      const eliteBonus = state.player.eliteDamageBonus ?? 0;
      if (eliteBonus !== 0) {
        const tgt = enemySystemState.enemies.find(e => e.id === enemyId);
        // BETA 20.89 — Stompatine (N11) + Elite Damage shrines read "+X% to
        // elites AND bosses", and the 19.98 comment claims it does — but the
        // gate was 'elite'-only, so bosses + the mega boss got NOTHING. Truth-
        // sync to the card: apply to elite OR boss variants.
        if (tgt && (tgt.variant === 'elite' || tgt.variant === 'boss')) {
          finalDmg *= 1 + eliteBonus / 100;
        }
      }
      _runDamageDealt += finalDmg;
      const result = damageEnemyFn(
        enemySystemState,
        enemyId,
        finalDmg,
        knockbackForce,
        knockbackAngle,
        1.0, // luck multiplier — neutral (1.0 = no bonus, no penalty)
      );

      // ── Item effects on hit ──
      const hitEnemy = enemySystemState.enemies.find(e => e.id === enemyId);
      const hitX = hitEnemy?.x ?? state.player.x;
      const hitY = hitEnemy?.y ?? state.player.y;
      const items = getItemState();

      // BETA 22.70 — weaponVfx hit effects: burn + aftermath decals.
      // BETA 22.74 — the burn is REAL now (Dylan approved 2026-07-02: 2s at
      // 6 dmg/s, halved vs elites/bosses). It rides the shared enemy burn
      // tick (0.5s cadence); burnSource routes telemetry so weapon burn is
      // never counted as Big Mac item damage. Strongest-burn-wins: we never
      // downgrade an active hotter burn (Big Mac scales past 6 dps).
      const _hitVfx = getWeaponVfx(weaponDefId ?? '');
      if (_hitVfx && hitEnemy && hitEnemy.alive) {
        // BETA 22.72 — flame-cone jet roar while enemies are cooking (the old
        // per-shot fire sound died with the projectile; hits are the new beat).
        if (weaponDefId === 'fireball_wand' && (state.time ?? 0) - _flameRoarLast > 1.0) {
          _flameRoarLast = state.time ?? 0;
          playFireballWandFire();
        }
        if ((_hitVfx.burnVisualSec ?? 0) > 0) {
          hitEnemy.burnUntil = Math.max(hitEnemy.burnUntil ?? 0, _hitVfx.burnVisualSec ?? 0);
          const _wBurnDps = _hitVfx.burnDps ?? 0;
          if (_wBurnDps > 0) {
            const _heavyTier = hitEnemy.variant === 'elite' || hitEnemy.variant === 'boss';
            const _dps = _wBurnDps * (_heavyTier ? 0.5 : 1.0);
            if (_dps > (hitEnemy.burnDps ?? 0)) {
              hitEnemy.burnDps = _dps;
              hitEnemy.burnSource = `weapon:${weaponDefId}`;
              // BETA 23.6x — CO-OP PHASE 2 part D: re-stamp attribution when the
              // weapon burn TAKES OVER the DoT (this branch is the only place the
              // active burn's dps/source change hands outside damageEnemy — the
              // stamp must move with burnSource or it points at the OLD applier).
              // The weapon system is single-local-shooter today, so the shooter
              // is always this machine's player. Cleared with the burn by the
              // enemySystem tick (stamp contract at enemies.ts burnAppliedBy).
              hitEnemy.burnAppliedBy = state.localPlayerId;
            }
          }
        }
        if (_hitVfx.hitDecal && Math.random() < _hitVfx.hitDecal.chance) {
          const [dMin, dMax] = _hitVfx.hitDecal.size;
          spawnGroundDecal(_hitVfx.hitDecal.kind, hitX, hitY, dMin + Math.random() * (dMax - dMin), state.time ?? 0);
        }
      }

      // NickMerc Jerk (R17): crit → AoE explosion at hit position
      if (isCrit && items) {
        const jerkSlot = items.inventory.find(s => s.item.id === 'R17');
        if (jerkSlot) {
          spawnCritExplosion(hitX, hitY, finalDmg, jerkSlot.stacks);
        }
      }

      // Gas Mask (R12): hitting a poisoned enemy gives overheal (max 20% of maxHp)
      // BETA 20.00 — UN-GATED from Debt (Dylan: healing/shield gain keeps running).
      // Shield > 0 deactivates Debt by the activation condition, which is intentional
      // now — recovering shield = Debt off = damage goes back to shield.
      if (items && hitEnemy && isPositionPoisoned(hitX, hitY)) {
        const gasMaskSlot = items.inventory.find(s => s.item.id === 'R12');
        if (gasMaskSlot) {
          const overhealGain = tuneOf('R12', 'overhealPerStack', 3) * gasMaskSlot.stacks;
          const overhealCap = Math.round(player.maxHp * tuneOf('R12', 'overhealCapPct', 0.2));
          if ((player.shield ?? 0) < overhealCap) {
            const beforeShield = player.shield ?? 0;
            player.shield = Math.min(overhealCap, beforeShield + overhealGain);
            if (player.maxShield < overhealCap) player.maxShield = overhealCap;
            // BETA 17.08 — PSBOT cycle-30t Tier C #10: track total overheal
            // amount granted this run on state (already in scope).
            const _sAnyDracs = state as unknown as Record<string, number | undefined>;
            _sAnyDracs._dracsGasMaskOverhealThisRun = (_sAnyDracs._dracsGasMaskOverhealThisRun ?? 0) + (player.shield - beforeShield);
          }
        }
      }

      // Play SFX based on result
      if (result.killed) {
        const deadEnemy = enemySystemState.enemies.find(e => e.id === enemyId);
        const spawnX = deadEnemy ? deadEnemy.x : state.player.x;
        const spawnY = deadEnemy ? deadEnemy.y : state.player.y;

        // Track whether this kill happened inside the visible viewport
        if (state.viewportW > 0) {
          const zoom = state.camera.zoom;
          recordKill(
            spawnX, spawnY,
            state.player.x, state.player.y,
            state.viewportW / (2 * zoom),
            state.viewportH / (2 * zoom),
          );
        }

        playEnemyDeath(spawnX, spawnY, deadEnemy?.definitionId, deadEnemy?.variant);
        // Item kill effects (Silly Juice stacks, etc.)
        recordItemKill();
        // BETA 15.26 — character passive kill hook. Returns extra drops to
        // append (Pirate Scavenger crates/health, Chad gold-in-aura). Also
        // increments per-character internal counters: Sniper crits, Silly
        // Juice stacks, Wizard Arcane Surge kill counter (5 → trigger).
        const charDrops = onPlayerKilledEnemy(state, spawnX, spawnY, isCrit);
        if (charDrops.length > 0) {
          result.drops.push(...charDrops);
        }
        // Anus Rocket (R08) — chance per stack to fire homing rocket from player
        // BETA 17.96 — Dylan: STRESS run STILL showed dozens of bursts blanketing
        // screen even after the 17.95 sqrt-blast-radius fix. Root cause: at
        // 100 stacks the geometric chance `1 - 0.85^100` is ~99.999% — every
        // single kill spawned a rocket. With 254 enemies dying fast at min-2820,
        // dozens of rockets exploded simultaneously. Switching to cube-root
        // exponent slows the proc-rate curve at extreme stacks:
        //   chance = 1 - 0.85^cbrt(stacks)
        //   stacks   1/4/16/100  →  15% / 24% / 36% / 53%
        // At 100 stacks ~half of kills proc — visible, satisfying, not screen-eating.
        {
          const rocketSlot = items?.inventory.find(s => s.item.id === 'R08');
          if (rocketSlot) {
            const chance = 1 - Math.pow(tuneOf('R08', 'procDecay', 0.85), Math.cbrt(rocketSlot.stacks));
            if (Math.random() < chance) {
              const px = state.player.x;
              const py = state.player.y;
              const rocketDmg = finalDmg * tuneOf('R08', 'dmgMult', 1.5) * rocketSlot.stacks;
              spawnAnusRocket(px, py, rocketDmg, rocketSlot.stacks);
              playAnusRocket(px, py);
            }
          }
        }
        // Always spawn loot orbs — guarantee at least 1 XP orb per kill
        if (state.lootOrbs) {
          if (result.drops.length > 0) {
            spawnDrops(state.lootOrbs, result.drops, spawnX, spawnY);
          } else {
            spawnDrops(state.lootOrbs, [{ type: 'xp', chance: 1, amount: 1 }], spawnX, spawnY);
          }
        }
        // Kill count tracked by enemyAdapter via enemyState.totalKills
      } else {
        const hitEnemy = enemySystemState.enemies.find(e => e.id === enemyId);
        const _hx = hitEnemy?.x ?? state.player.x, _hy = hitEnemy?.y ?? state.player.y;
        // BETA — weapons with their own impact sound play it; otherwise fall back
        // to the enemy-type hit grunt. (playWeaponHit returns true when handled.)
        if (!playWeaponHit(weaponDefId ?? '', _hx, _hy)) {
          // Chad Aura → grunt only, NO hitmarker ting (Dylan: aura = just the grunt).
          playEnemyHit(_hx, _hy, hitEnemy?.definitionId, hitEnemy?.variant, weaponDefId === 'chad_aura');
        }
      }
      // Lifesteal
      // BETA 20.00 — UN-GATED from Debt (Dylan: healing keeps running, Debt
      // is a race between heal-rate and gold-drain-rate).
      if (player.lifesteal > 0 && player.hp > 0) {
        // Cap at 8 HP per hit so lifesteal can't trivialise the game.
        // hp > 0 guard: a dead player must not lifesteal back to life
        // (sibling pendingLifestealHeal path already guards hp > 0).
        // BETA 21.23 — "vampire when wounded" (Dylan): lifesteal scales with MISSING
        // HP, NOT a flat cap. ZERO above 90% HP (you literally can't top off — any
        // firehose can't make you immortal at full), ramps to full strength at ~25%
        // HP, and SURGES (×1.2) when near death for a clutch comeback. So a lifesteal
        // build hovers in the danger band and god mode can finally finish you.
        const _woundFactor = Math.max(0, Math.min(1.2, (0.9 - player.hp / Math.max(1, player.maxHp)) / 0.65));
        // BETA 21.76 — Dylan: lifesteal was a FLAT 8 HP/hit cap, so EVERY hit healed
        // the same 8 no matter the weapon/damage, and a swarm (100s of hits/sec)
        // firehosed you immortal. Now it's a TRUE % of THIS hit's damage (scales with
        // weapon) AND a shared PER-SECOND cap (5% of max HP/sec, across BOTH lifesteal
        // paths) bounds the total so volume can't out-heal a swarm. The 90%-HP wound
        // gate stays — lifesteal is a comeback tool, not an immortality engine.
        const _lsS = state as unknown as { _lsWindowStart?: number; _lsHealedThisWindow?: number };
        { const _el = state.time - (_lsS._lsWindowStart ?? -999); if (_el >= 1 || _el < 0) { _lsS._lsWindowStart = state.time; _lsS._lsHealedThisWindow = 0; } }  // BETA 21.78 — also reset if time went backward (floor-advance resets state.time to 0)
        const _lsRemain = Math.max(0, player.maxHp * 0.05 - (_lsS._lsHealedThisWindow ?? 0));
        const heal = Math.min(damage * (player.lifesteal / 100) * _woundFactor, _lsRemain);
        const beforeHp = player.hp;
        player.hp = Math.min(player.maxHp, player.hp + heal);
        const actualHealed = player.hp - beforeHp;
        _lsS._lsHealedThisWindow = (_lsS._lsHealedThisWindow ?? 0) + actualHealed;
        // BETA 16.55 — PSBOT cycle-30l Bug A: R05 / N01 / lifesteal-stat
        // healing was NEVER feeding _healingReceivedThisRun. Cycle-30i Bug C
        // fix only wired the WEAPON-stat path (pendingLifestealHeal commit
        // at weaponAdapter.ts:442) — this player-stat path was missed.
        // Result: Killa run with 11% lifesteal × 779K dmg should have
        // healed ~108K HP and showed runHealingReceived=0 in live_play.
        // Now this path also increments the counter.
        if (actualHealed > 0) {
          const _sAny = state as unknown as Record<string, number | undefined>;
          _sAny._healingReceivedThisRun = (_sAny._healingReceivedThisRun ?? 0) + actualHealed;
          state._healEventsThisRun = (state._healEventsThisRun ?? 0) + 1;
        }
      }
    };

    // Keep audio listener locked to player position for spatial sound
    updateAudioListener(player.x, player.y);

    // Initialize SFX if not yet done
    if (!sfxInitialized) {
      const audioCtx = getAudioContext();
      const sfxNode = getSfxGainNode();
      if (audioCtx && sfxNode) {
        initGameSfx(audioCtx, sfxNode);
        sfxInitialized = true;
      }
    }

    // BETA 15.67 — track ammo + reload state PER WEAPON SLOT (was: only weapons[0]).
    // Bug: if slot 0 was an aura (chad_aura) or any non-firing weapon, ALL weapon
    // firing sounds went silent because slot 0's ammo never decremented. Fixed by
    // capturing arrays of prev state across every equipped slot.
    const prevAmmoBySlot = weaponState.weapons.map(w => w?.currentAmmo ?? 0);
    const prevReloadingBySlot = weaponState.weapons.map(w => w?.isReloading ?? false);
    const prevArcSlashCount = weaponState.arcSlashes.length;

    // Compute min(halfW, halfH) in world units — the shorter viewport dimension.
    // Enemies spawn just OUTSIDE this edge, so capping targeting range here
    // guarantees every enemy is already on-screen before any weapon can target it.
    const zoom = state.camera.zoom;
    const viewHalfMin = state.viewportW > 0
      ? Math.min(state.viewportW / (2 * zoom), state.viewportH / (2 * zoom))
      : undefined;

    // BETA 13.95 — wire status-effect callback so weapons can apply freeze/slow/wet on hit
    const onApplyStatus = (
      enemyId: number,
      effect: 'freeze' | 'slow' | 'wet',
      duration: number,
      slowMultiplier: number = 0.5,
    ) => {
      if (enemySystemState) applyEnemyStatusFn(enemySystemState, enemyId, effect, duration, slowMultiplier);
    };

    // BETA 13.97 — wire armor/shield-strip callback (Ultra Beam)
    const onApplyArmorStrip = (enemyId: number, shieldStrip: number, armorStrip: number) => {
      if (enemySystemState) applyEnemyArmorShieldStripFn(enemySystemState, enemyId, shieldStrip, armorStrip);
    };

    // BETA 16.08 — Line-of-sight callback for aura/arc/orbital weapons.
    // Set every frame so the closure captures the current state ref.
    weaponState.hasLineOfSight = (ax, ay, bx, by) => hasLineOfSight(state, ax, ay, bx, by);

    // Update weapons
    updateWeapons(weaponState, dt, playerForWeapon, aliveEnemies, onEnemyHit, viewHalfMin, onApplyStatus, onApplyArmorStrip);

    // BETA 13.95 — apply accumulated lifesteal heal to player.hp, then reset.
    // BETA 15.14 — GATED during N10 Debt window. Per Dylan: "there has to
    // be a pause on anything healing them including life steal." The
    // lifesteal accumulator continues to grow during Debt but the apply is
    // skipped until the 4s window expires; at that point the pending pool
    // applies normally on the next tick.
    // BETA 16.04 — ALSO gated when hp <= 0. Same fix as engine.ts hpRegen
    // / VooGoo / Mom's Cooking: a dead player can't heal back to alive
    // before the engine's death check fires. Pending lifesteal accumulator
    // is reset to 0 (vs preserved during Debt) because the player is
    // about to die — there's no future tick where this should apply.
    // (BETA 22.90 — the unused _isInDebt compat local is gone with the Debt
    // mechanic; lifesteal was already un-gated since 20.00.)
    if (weaponState.pendingLifestealHeal > 0 && state.player.hp > 0) {
      const before = state.player.hp;
      // BETA 21.23 — same "vampire when wounded" scaling as the player-stat path.
      const _woundFactor = Math.max(0, Math.min(1.2, (0.9 - state.player.hp / Math.max(1, state.player.maxHp)) / 0.65));
      // BETA 21.76 — share the player-stat path's per-second lifesteal cap (5% max HP/sec)
      // so innate weapon lifesteal (Drac's Fang etc.) + the stat path can't COMBINE to firehose.
      const _lsS = state as unknown as { _lsWindowStart?: number; _lsHealedThisWindow?: number };
      { const _el = state.time - (_lsS._lsWindowStart ?? -999); if (_el >= 1 || _el < 0) { _lsS._lsWindowStart = state.time; _lsS._lsHealedThisWindow = 0; } }  // BETA 21.78 — also reset if time went backward (floor-advance resets state.time to 0)
      const _lsRemain = Math.max(0, state.player.maxHp * 0.05 - (_lsS._lsHealedThisWindow ?? 0));
      const _lsHeal = Math.min(weaponState.pendingLifestealHeal * _woundFactor, _lsRemain);
      state.player.hp = Math.min(state.player.maxHp, state.player.hp + _lsHeal);
      const healed = state.player.hp - before;
      _lsS._lsHealedThisWindow = (_lsS._lsHealedThisWindow ?? 0) + healed;
      if (healed > 0) {
        const _sAny = state as unknown as Record<string, number | undefined>;
        _sAny._healingReceivedThisRun = (_sAny._healingReceivedThisRun ?? 0) + healed;
        state._healEventsThisRun = (state._healEventsThisRun ?? 0) + 1;
      }
      weaponState.pendingLifestealHeal = 0;
    } else if (weaponState.pendingLifestealHeal > 0 && state.player.hp <= 0) {
      weaponState.pendingLifestealHeal = 0; // dead — discard pending heal
    }

    // BETA 16.01 — RE-ENABLED. I (PS Major) misread Dylan's earlier "now
    // weapon should be able to shoot throuh collision" as a feature
    // request when it was actually a BUG REPORT. Per Dylan's follow-up:
    // "i ama still able to shoot through walls when i am close by them.
    // fix!! room walls now... as of right now i can press against the
    // wall in a room and shoot through it."
    //
    // Original 15.40 swept raycast wall-kill restored: step from prev →
    // current in 16wu increments and kill the projectile at the first
    // non-walkable cell, so bullets always stop AT the wall surface.
    // Plus: 16.01 ALSO checks the spawn position. If the projectile
    // spawns INSIDE a wall (player pressed against it), the very first
    // raycast step will detect the spawn cell as non-walkable and kill
    // the proj on frame 0. Previously the spawn was inside the wall and
    // the raycast walked OUTWARD into walkable space, so the kill never
    // triggered = pressed-against-wall shoot-through.
    if (state.map?.roomMap) {
      for (const proj of weaponState.projectiles) {
        if (!proj.alive) continue;
        // BETA 16.01 — spawn-inside-wall check. If the current position
        // is inside a wall AND the previous position was also inside (or
        // is the same — first frame), kill immediately.
        if (!isWalkable(state, proj.x, proj.y)) {
          spawnImpactAoe(weaponState, proj.weaponId, proj.x, proj.y);
          spawnImpactExplosion(proj.weaponId, proj.x, proj.y, proj.damage, proj.isCrit, aliveEnemies, onEnemyHit);
          proj.alive = false;
          continue;
        }
        const dx = proj.x - proj.prevX;
        const dy = proj.y - proj.prevY;
        const moveLen = Math.sqrt(dx * dx + dy * dy);
        const STEP = 16; // wu — finer than wall thickness (96wu) to prevent tunneling
        const steps = Math.max(1, Math.ceil(moveLen / STEP));
        for (let s = 1; s <= steps; s++) {
          const t = s / steps;
          const sx = proj.prevX + dx * t;
          const sy = proj.prevY + dy * t;
          if (!isWalkable(state, sx, sy)) {
            // BETA 16.24 — PSBOT cycle-25 P0.10: WALL BOUNCE. Per Dylan +
            // PSBOT audit: shrine "+1 Projectile Bounce" was NOT triggering
            // a wall bounce; projectiles died on wall contact. bouncesLeft
            // was only used for enemy-to-enemy chains. Now: if bouncesLeft
            // > 0 at wall impact, reflect the velocity along the impact
            // normal, decrement bouncesLeft, and keep the projectile
            // alive. Detect axis by sampling both sides of the impact
            // point — the unwalkable side is the wall direction.
            if (proj.bouncesLeft > 0) {
              // Sample 8 wu in each cardinal from the impact point to find
              // which axis the wall is blocking. If both axes are blocked
              // (corner hit), reflect both.
              const eps = 8;
              const blockedHorizontal = !isWalkable(state, sx + Math.sign(dx) * eps, sy);
              const blockedVertical   = !isWalkable(state, sx, sy + Math.sign(dy) * eps);
              if (blockedHorizontal) proj.velocityX = -proj.velocityX;
              if (blockedVertical)   proj.velocityY = -proj.velocityY;
              // If neither axis sample landed in the wall (rare with
              // coarse stepping), flip the dominant component as fallback.
              if (!blockedHorizontal && !blockedVertical) {
                if (Math.abs(dx) > Math.abs(dy)) proj.velocityX = -proj.velocityX;
                else proj.velocityY = -proj.velocityY;
              }
              // Push projectile back along the inverted velocity slightly
              // so it doesn't re-collide on the next sub-step.
              const pushBack = STEP * 0.5;
              const vMag = Math.hypot(proj.velocityX, proj.velocityY) || 1;
              proj.x = sx + (proj.velocityX / vMag) * pushBack;
              proj.y = sy + (proj.velocityY / vMag) * pushBack;
              proj.bouncesLeft--;
              if (process.env.NODE_ENV !== 'production') {
                console.log('[PROJ BOUNCE]', { weaponId: proj.weaponId, bouncesLeft: proj.bouncesLeft, axis: blockedHorizontal ? (blockedVertical ? 'corner' : 'horizontal') : 'vertical' });
              }
              break; // exit step loop; next frame's anti-tunneling will
                     // continue from the bounce point.
            }
            // BETA 13.97 — wall impact spawns AoE ground effect at hit point
            // (smoke cloud / slow puddle). Use the SURFACE position (sx,sy),
            // not the over-shoot position, so the puddle paints AT the wall.
            spawnImpactAoe(weaponState, proj.weaponId, sx, sy);
            spawnImpactExplosion(proj.weaponId, sx, sy, proj.damage, proj.isCrit, aliveEnemies, onEnemyHit);
            proj.x = sx;
            proj.y = sy;
            proj.alive = false;
            break;
          }
        }
      }
    }

    // BETA 15.77 — Detect arc/melee swing per-weapon by inspecting the NEW
    // arc slash entry's spriteUrl. Currently only the spatula is an arc weapon.
    // BETA 22.76 — REMOVED the dracs_fang branch here: it was DEAD CODE (22.75
    // audit). Drac's Fang became a THROWABLE projectile (weapons.ts throwable +
    // boomerangReturn, no arc flag) back in 19.45, so it never creates an arc
    // slash — arcSlashes never grew for it and this sound never played. Its
    // throw sound is now dispatched from the ammo-decrement fire switch below
    // (same path Terminator uses).
    if (weaponState.arcSlashes.length > prevArcSlashCount) {
      const newSlash = weaponState.arcSlashes[weaponState.arcSlashes.length - 1];
      const sprUrl = newSlash?.spriteUrl ?? '';
      if (sprUrl.includes('spatula_slash')) {
        playSpatulaSlash();
      } else if (sprUrl.includes('chain_whip')) {
        playChainWhipSwing();   // BETA 22.82 — Chain Whip is an arc weapon; its clang goes here, not the fire switch
      }
    }

    // BETA 15.74 — Per-weapon FIRE sound dispatch. Same philosophy as
    // 15.69 reload sounds: each weapon plays its OWN sound, and weapons
    // without a Dylan-provided sample fire SILENTLY (no fallback). PSBOT
    // RUN Audio Batch #1 added 5 weapon-specific samples; Ultra Beam has
    // its own special charge-up timing (audio plays here at trigger so
    // the BLAM lands at projectile spawn 0.737s later — chargeUpMs in
    // the weapon definition handles the projectile-spawn delay separately).
    // Ugly Kittens plays the spawn meow on each fire — the attack hit sound
    // is wired in weaponSystem.ts onEnemyHit.
    for (let i = 0; i < weaponState.weapons.length; i++) {
      const w = weaponState.weapons[i];
      if (!w) continue;
      const defId = w.definitionId;
      if (defId === 'spatula_slash') continue; // arc — handled above by arcSlashes count
      const newAmmo = w.currentAmmo;
      const newReloading = w.isReloading;
      const prevA = prevAmmoBySlot[i] ?? 0;
      const prevR = prevReloadingBySlot[i] ?? false;

      if (newAmmo < prevA && !prevR) {
        switch (defId) {
          case 'revolver':            playRevolverShot();   break;
          case 'peanut_rifle':        playRifleShot();      break;
          case 'tactical_basketball': playBasketballBounce(); break;
          case 'blunderbuss':         playBlunderbussFire(); break;
          case 'viking_axe':          playVikingAxeThrow();  break;
          case 'voogoo_rager_toss':   playVoogooRagerThrow(); break;
          case 'fireball_wand':       playFireballWandFire(); break;
          case 'wpn_the_terminator':  playTerminatorFire();   break;
          // wpn_ultra_beam: audio plays in weaponSystem at charge-up START
          // (so the BLAM in the audio aligns with projectile spawn 0.737s
          // later). Don't play it here on ammo-decrement.
          case 'wpn_ugly_kittens':    /* BETA 21.53 — Dylan: drop the spawn meow; only the on-attack cat-fight sound (weaponSystem onEnemyHit) plays. */ break;
          case 'wpn_poop_sling':      playPoopSlingFire();    break;
          case 'wpn_squirter':        playSquirterFire();     break;
          case 'wpn_frozen_darts':    playFrozenDartsFire();  break;
          case 'wpn_shitty_cannon':   playShittyCannonFire(); break;
          case 'wpn_ex_girlfriend':   playExGirlfriendFire(); break;
          case 'wpn_hurray':          playHurrayFire();       break;
          // BETA 15.90 — Per Dylan: "rpk audio not working. that audio it
          // was using is just fine. not sure why its missing." Was using the
          // revolver_shot fallback before 15.74; the 15.74 "no fallback"
          // pattern accidentally silenced it because there was no explicit
          // case. Re-routing to playRevolverShot() until Dylan provides a
          // dedicated RPK sample. Same intent as the legacy fallback —
          // RPK keeps its sound, no other weapon is silently fallback-pulled.
          case 'rpk_machine_gun':     playRpkShot();          break;
          // BETA 22.76 — Drac's Fang throw sound (was DEAD via the removed
          // arcSlashes branch — the weapon is a throwable projectile, not an
          // arc). Same ammo-decrement path Terminator uses.
          case 'wpn_dracs_fang':      playDracsFangSwing();    break;
          // BETA 22.82 — the guns that used to fire SILENTLY (22.75 audit).
          case 'wpn_smg':             playSmgFire();          break;
          case 'wpn_rpg':             playRpgFire();          break;
          case 'wpn_crossbow':        playCrossbowFire();     break;
          case 'wpn_railgun':         playRailgunFire();      break;
          default: /* silent — no Dylan-provided sample for this weapon yet */ break;
        }
      }
      // BETA 15.93 — Reload sounds removed entirely. Per Dylan: "lets get
      // rid of reload sounds. its to much. like when guns reload."
      // Previously (15.69): only revolver + rifle had reload SFX. Now: none.
      // Detection block left for future re-enable but the calls are gone.
      if (newReloading && !prevR) {
        // Reload SFX intentionally silenced per Dylan ask. If you want to
        // restore: uncomment + re-import playRifleReload / playRevolverReload.
      }
    }
  },

  render(ctx: CanvasRenderingContext2D, state: GameState, _viewBounds: ViewBounds) {
    if (!weaponState) return;

    // Build camera interface for weapon rendering
    // The weapon system does its own world→screen conversion
    const camera: CameraForWeapon = {
      x: state.camera.x,
      y: state.camera.y,
      viewportWidth: canvasWidth,
      viewportHeight: canvasHeight,
      zoom: state.camera.zoom,
    };

    // BETA 16.24 — pass bonusProjectileCount + bonusAttackSpeedPercent
    // through so renderOrbitWeapons (Karen Drones) can render N drones at
    // attack-speed-scaled spin.
    // BETA 23.01 — WEAPON DEPTH: attached sprites draw inside the player
    // render group (renderOrbitWeaponSpritesPass below); this call keeps
    // projectiles / trails / muzzle flashes ABOVE the world (Dylan rule 2).
    renderWeapons(weaponState, ctx, camera, {
      x: state.player.x,
      y: state.player.y,
      bonusProjectileCount: state.player.projectileCountBonus ?? 0,
      bonusAttackSpeedPercent: state.player.attackSpeedBonus ?? 0,
    }, WEAPON_DEPTH_GROUP ? 'skip' : 'all');
  },
};

/** BETA 23.01 — attached weapon sprites, drawn inside the player render
 *  group ('behind' before Greg's sprite, 'front' after) so they inherit his
 *  depth vs machines/props. */
export function renderOrbitWeaponSpritesPass(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  which: 'behind' | 'front',
): void {
  if (!weaponState || !WEAPON_DEPTH_GROUP) return;
  renderOrbitWeaponSprites(weaponState, ctx, {
    x: state.camera.x, y: state.camera.y,
    viewportWidth: canvasWidth, viewportHeight: canvasHeight,
    zoom: state.camera.zoom,
  }, {
    x: state.player.x, y: state.player.y,
    bonusProjectileCount: state.player.projectileCountBonus ?? 0,
    bonusAttackSpeedPercent: state.player.attackSpeedBonus ?? 0,
  }, which);
}

/** BETA 23.01 — feed solid-prop visual blocking zones to the smart-slide
 *  (machines near the player; P5 extends to every solid prop). */
export function updateWeaponOcclusionZones(state: GameState): void {
  const zones: WeaponOcclusionZone[] = [];
  const px = state.player.x, py = state.player.y;
  for (const c of state.map.roomMap.chests) {
    if (c.state === 'gone' || c.type === 'elite') continue;
    if (Math.abs(c.x - px) > 400 || Math.abs(c.y - py) > 400) continue;
    // machine cabinet VISUAL zone (wider + taller than the foot collider)
    zones.push({ x0: c.x - 50, y0: c.y - 120, x1: c.x + 50, y1: c.y + 38 });   // 23.29: machine ×0.8
  }
  // BETA 23.13 — P5 delivered: solid STANDING props (cars, dumpsters, piles)
  // block/slide weapons the same way. Thin/small props (cones, lamps) are
  // skipped — sliding around a cone looks broken, and the lamp pole is
  // narrower than the gun sprite.
  if (MAP_PROPS && BOUNDLESS_MAP) {
    const _seed = state.map.roomMap.seed ?? 1;
    for (const p of propsInRect(px - 400, py - 400, px + 400, py + 400, _seed)) {
      if (p.def.kind !== 'standing' || !p.def.foot) continue;
      const aspect = propAspect(p.def.url) ?? 1;
      const w = p.def.h * aspect;
      if (w < 70) continue;
      zones.push({ x0: p.x - w * 0.5, y0: p.y - p.def.h * 0.82, x1: p.x + w * 0.5, y1: p.y + 8 });
    }
  }
  setWeaponOcclusionZones(zones);
}

/** Render aura in world-space — call INSIDE the camera transform, BEFORE player sprite. */
export function renderAuraWeaponsBelow(
  ctx: CanvasRenderingContext2D,
  state: GameState,
): void {
  if (!weaponState) return;
  renderAuraInWorldSpace(weaponState, ctx, state.camera.zoom, { x: state.player.x, y: state.player.y });
}

/** Render arc slash visuals ON TOP of enemies — call from engine after renderEnemies. */
export function renderWeaponArcSlashOverlay(
  ctx: CanvasRenderingContext2D,
  state: GameState,
): void {
  if (!weaponState) return;
  const camera: CameraForWeapon = {
    x: state.camera.x,
    y: state.camera.y,
    viewportWidth: canvasWidth,
    viewportHeight: canvasHeight,
    zoom: state.camera.zoom,
  };
  // BETA 15.54 — pass live player position so the swing visual follows the
  // player during the 0.35s flash (Bug 2: "swiping sprite looks like it's
  // lagging" — was using snapshot at attack-spawn).
  renderArcSlashes(weaponState, ctx, camera, state.player.x, state.player.y);
}

/** BETA 15.49 — Render the persistent attack-zone preview (sector + weapon
 *  sprite) for each equipped arc/melee weapon (Spatula, Drac's Fang). Shows
 *  attack reach, swing direction, and the weapon visually pointing wherever
 *  the player is moving. Call from engine.ts BEFORE the player layer so the
 *  player sprite renders on top of the faded weapon preview.
 *  BETA 15.54 — passes attackAreaBonus so sprite + sector scale with size buffs. */
export function renderWeaponArcIdleOverlay(
  ctx: CanvasRenderingContext2D,
  state: GameState,
): void {
  if (!weaponState) return;
  const camera: CameraForWeapon = {
    x: state.camera.x,
    y: state.camera.y,
    viewportWidth: canvasWidth,
    viewportHeight: canvasHeight,
    zoom: state.camera.zoom,
  };
  renderArcWeaponIdle(
    weaponState, ctx, camera,
    state.player.x, state.player.y,
    lastFacingAngle,
    state.player.attackAreaBonus ?? 0,
  );
}
